import { exportChangeLogColours, setChangeLogComment } from '@components/export/export.config';
import * as GC from '@grapecity/spread-sheets';
import { IAcquisition } from '@models/acquisition';
import { ICalibrationChange } from '@models/calibrations/calibration-changelog';
import { IDecayCalibration } from '@models/decay/decay-calibration';
import { DecayCalibrationType, IDecayCalibrationType } from '@models/decay/decay-calibration-type';
import { IFunctionalForm } from '@models/functional-form';
import { IProduct } from '@models/product';
import { CalibrationStatus } from '@models/record-status';
import { getLastObservationDateFromCalibration } from '@shared/dates';
import { getAcquisitionNames } from '@shared/get-acquisition-names';
import { setBlankCellValue } from '@shared/spread/sheet-functions';
import { borders, colours, DASH, FONT_NAME, FONT_SIZE } from '@shared/spread/spread-settings';
import { changeLogColours, getCalibrationRecordStatusBorderColor, getChangeLogSummary, getDateForDisplay } from '../helpers';

let _functionalForms: IFunctionalForm[] = []; // TODO: Get rid of It. Use parameters instead
let _acquisitions: IAcquisition[] = []; // TODO: Get rid of It. Use parameters instead

export const addDecayChangeLogToSheet = (
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  product: IProduct,
  calibrations: IDecayCalibration[],
  functionalForms: IFunctionalForm[],
  acquisitions: IAcquisition[],
  calibrationTypes: IDecayCalibrationType[],
  getDecayChangeLogDiff: (calibrationOriginal: IDecayCalibration, calibrationNew: IDecayCalibration) => ICalibrationChange[]
): number => {
  try {
    _functionalForms = functionalForms; // TODO: Get rid of It. Use parameters instead
    _acquisitions = acquisitions; // TODO: Get rid of It. Use parameters instead

    rowIndex = createHeaders(sheet, rowIndex, startCol);
    [...calibrations].forEach((calibration: IDecayCalibration, levelOneIndex: number) => {
      if (levelOneIndex === calibrations.length - 1) {
        rowIndex = createLevelOneCalibration(sheet, rowIndex, startCol, calibration, calibrationTypes);
      } else {
        rowIndex = createLevelOneCalibration(
          sheet,
          rowIndex,
          startCol,
          calibration,
          calibrationTypes,
          getDecayChangeLogDiff(calibration, calibrations[levelOneIndex + 1])
        );
      }

      // Fix 7641: The last inactive record should not be expanded in the export
      if (calibration.RecordStatus !== CalibrationStatus.Inactive) {
        calibration.ChangeLogs.forEach((changeLog, ind) => {
          const isLastOfList = ind === calibration.ChangeLogs.length - 1;
          rowIndex = createLevelTwoCalibration(sheet, rowIndex, startCol, changeLog, isLastOfList);
        });
      }
    });
  } catch (err) {
    console.error(`Error building the Decay Change Log`, { sheet, product, calibrations, functionalForms, acquisitions, rowIndex }, err);
  }
  return rowIndex;
};

/**
 * Responsible for creating sheet and related styling
 */
function createHeaders(sheet: GC.Spread.Sheets.Worksheet, rowIndex: number, startCol: number): number {
  const rowSpan = 2;
  let colSpan = 2;
  let takenCols = 0;

  // Creating headers
  {
    // Decay Calibration Types
    {
      colSpan = 2;
      sheet.getCell(rowIndex, startCol + takenCols).text('Calibration Type');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }
    // Excluded Acquisitions
    {
      colSpan = 3;
      sheet.getCell(rowIndex, startCol + takenCols).text('Excluded Acquisitions');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // Vintage Start
    {
      colSpan = 2;
      sheet.getCell(rowIndex, startCol + takenCols).text('Start Date');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // Last Observation Date
    {
      colSpan = 2;
      sheet.getCell(rowIndex, startCol + takenCols).text('Last Observation Date');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // Model
    {
      colSpan = 1;
      sheet.getCell(rowIndex, startCol + takenCols).text('Model');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // Start Period
    {
      colSpan = 1;
      sheet.getCell(rowIndex, startCol + takenCols).text('Start Period');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // End Period
    {
      colSpan = 1;
      sheet.getCell(rowIndex, startCol + takenCols).text('End Period');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // Curtailment
    {
      colSpan = 1;
      sheet.getCell(rowIndex, startCol + takenCols).text('Curtailment');
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }
  }

  // Styling
  {
    const headerRange = sheet.getRange(rowIndex, startCol, rowSpan, takenCols);
    headerRange
      .backColor(changeLogColours.HeaderColour)
      .foreColor('white')
      .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center);
    headerRange.setBorder(borders.GREY_NORMAl, { all: true });
  }

  rowIndex += 2;
  return rowIndex;
}

function createLevelOneCalibration(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  calibration: IDecayCalibration,
  calibrationTypes: IDecayCalibrationType[],
  changedProps?: ICalibrationChange[]
): number {
  let colSpan = 2;
  const rowSpan = 2;
  const takenRows = rowSpan;
  let takenCols = 0;

  // Acquisition type
  {
    sheet.getCell(rowIndex, startCol + takenCols).text(getDecayCalibrationTypeName(calibration.DecayCalibrationType, calibrationTypes));
    colSpan = 2;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'DecayCalibrationType');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }
    takenCols += colSpan;
  }
  // Excluded Acquisitions
  {
    sheet.getCell(rowIndex, startCol + takenCols).text(getAcquisitionNames(_acquisitions, calibration.ExcludeAcquisitionIds));
    colSpan = 3;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'ExcludeAcquisitionIds');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }
    takenCols += colSpan;
  }

  // Vintage Start
  {
    sheet.getCell(rowIndex, startCol + takenCols).text(getDateForDisplay(new Date(calibration.StartDate)));
    colSpan = 2;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'StartDate' || property === 'StartVintage');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }
    takenCols += colSpan;
  }

  // Last Observation Date
  {
    const lastObsDate = getLastObservationDateFromCalibration(calibration);
    const cell = sheet.getCell(rowIndex, startCol + takenCols);
    if (lastObsDate) {
      cell.value(lastObsDate);
    } else {
      setBlankCellValue(cell);
    }
    colSpan = 2;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    takenCols += colSpan;
  }

  // Chosen estimate values
  if (calibration.DecayCalibrationType !== DecayCalibrationType.AvgClosedAccounts) {
    const chosenEstimate = calibration.DecayEstimateSetting.find(({ IsChosen }) => IsChosen);
    if (chosenEstimate) {
      // Model
      {
        sheet.getCell(rowIndex, startCol + takenCols).text(getFunctionalForm(chosenEstimate.FunctionalForm));
        colSpan = 1;
        sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
        if (changedProps && changedProps.length > 0) {
          const change = changedProps.find(({ property }) => property === 'FunctionalForm');
          if (change) {
            sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
          }
        }
        takenCols += colSpan;
      }

      // Start Period
      {
        sheet.getCell(rowIndex, startCol + takenCols).text(chosenEstimate.StartObservation.toString());
        colSpan = 1;
        if (changedProps && changedProps.length > 0) {
          const change = changedProps.find(({ property }) => property === 'StartObservation');
          if (change) {
            sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
          }
        }
        sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
        takenCols += colSpan;
      }

      // End Period
      {
        sheet.getCell(rowIndex, startCol + takenCols).text(chosenEstimate.EndObservation.toString());
        colSpan = 1;
        sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
        if (changedProps && changedProps.length > 0) {
          const change = changedProps.find(({ property }) => property === 'EndObservation');
          if (change) {
            sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
          }
        }
        takenCols += colSpan;
      }

      // Curtailment
      sheet
        .getCell(rowIndex, startCol + takenCols)
        .text(chosenEstimate.Curtailment.toString())
        .borderRight(borders.BLACK_THICK);
      colSpan = 1;
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      if (changedProps && changedProps.length > 0) {
        const change = changedProps.find(({ property }) => property === 'Curtailment');
        if (change) {
          sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
        }
      }
      takenCols += colSpan;
    } else {
      console.error(`Chosen estimate missing in`, { calibration });
    }
  } else {
    // Model
    {
      sheet.getCell(rowIndex, startCol + takenCols).text(DASH);
      colSpan = 1;
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      if (changedProps && changedProps.length > 0) {
        const change = changedProps.find(({ property }) => property === 'FunctionalForm');
        if (change) {
          sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
        }
      }
      takenCols += colSpan;
    }

    // Start Period
    {
      sheet.getCell(rowIndex, startCol + takenCols).text(DASH);
      colSpan = 1;
      if (changedProps && changedProps.length > 0) {
        const change = changedProps.find(({ property }) => property === 'StartObservation');
        if (change) {
          sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
        }
      }
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      takenCols += colSpan;
    }

    // End Period
    {
      sheet.getCell(rowIndex, startCol + takenCols).text(DASH);
      colSpan = 1;
      sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
      if (changedProps && changedProps.length > 0) {
        const change = changedProps.find(({ property }) => property === 'EndObservation');
        if (change) {
          sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
        }
      }
      takenCols += colSpan;
    }

    // Curtailment
    sheet
      .getCell(rowIndex, startCol + takenCols)
      .text(calibration.ImpliedCurtailment ? calibration.ImpliedCurtailment.toString() : DASH)
      .borderRight(borders.BLACK_THICK);
    colSpan = 1;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    if (changedProps && changedProps.length > 0) {
      const change = changedProps.find(({ property }) => property === 'Curtailment' || property === 'ImpliedCurtailment');
      if (change) {
        sheet.getCell(rowIndex, startCol + takenCols).backColor(colours.LIGHT_GREEN);
      }
    }
    takenCols += colSpan;
  }

  // Styling
  {
    const rowRange = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
    rowRange.hAlign(GC.Spread.Sheets.HorizontalAlign.center);
    rowRange.vAlign(GC.Spread.Sheets.VerticalAlign.center);
    rowRange.setBorder(borders.BLACK_MEDIUM, { all: true });
    rowRange.setBorder(getCalibrationRecordStatusBorderColor(calibration.RecordStatus), { left: true });
  }

  rowIndex += takenRows;
  return rowIndex;
}

function createLevelTwoCalibration(
  sheet: GC.Spread.Sheets.Worksheet,
  rowIndex: number,
  startCol: number,
  changeLog: any,
  isLastOfList: boolean
): number {
  let takenCols = 0;
  let colSpan = 1;
  const rowSpan = 1;
  const takenRows = rowSpan;

  // User info
  {
    const date = new Date(changeLog.DateTime.replace('T', 'Z')).toString().substr(0, 21);
    sheet
      .getCell(rowIndex, startCol + takenCols)
      .text(`${changeLog.UserName || 'User ???'} on ${date}`)
      .foreColor(exportChangeLogColours.levelTwo.text)
      .font(`${FONT_SIZE}pt ${FONT_NAME}`);
    colSpan = 3;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    takenCols += colSpan;
  }
  // Comment
  {
    setChangeLogComment(sheet.getCell(rowIndex, startCol + takenCols), changeLog.Comment);
    colSpan = 7;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    takenCols += colSpan;
  }
  // Summary
  {
    // TODO track down the summary
    const summary = getChangeLogSummary(changeLog.Summary);
    sheet
      .getCell(rowIndex, startCol + takenCols)
      .text(summary)
      .foreColor(exportChangeLogColours.levelTwo.summary)
      .font(`${FONT_SIZE}pt ${FONT_NAME}`);
    colSpan = 3;
    sheet.addSpan(rowIndex, startCol + takenCols, rowSpan, colSpan);
    takenCols += colSpan;
  }

  // ChangeLog Level Styling
  {
    const rowRange = sheet.getRange(rowIndex, startCol, takenRows, takenCols);
    rowRange
      .hAlign(GC.Spread.Sheets.HorizontalAlign.center)
      .vAlign(GC.Spread.Sheets.VerticalAlign.center)
      .backColor(changeLogColours.LevelTwoColour);
    rowRange.setBorder(borders.BLACK_THIN, { all: true });
    rowRange.setBorder(borders.BLACK_MEDIUM, { left: true, right: true });
    rowRange.setBorder(borders.BLACK_MEDIUM, { left: true, right: true, bottom: isLastOfList });
  }

  rowIndex += takenRows;

  return rowIndex;
}

export const getFunctionalForm = (functionalFormNum: number): string => {
  const foundFunctionalForm = _functionalForms.find(ff => ff.Index === functionalFormNum);
  return foundFunctionalForm.Name;
};

export const getDecayCalibrationTypeName = (id: number, decayCalibrationTypes: IDecayCalibrationType[]) => {
  const foundMatch = decayCalibrationTypes.find(type => type.Id === id);
  return foundMatch ? foundMatch.Name : decayCalibrationTypes[0].Name;
};
